import * as THREE from 'three'
import { FrameSelect, FrameSelectMode } from '@/plugins/cad/module/core/selector/FrameSelect'
import type { CADViewManager } from '../manager/CADViewManager'
import type { IEntity } from '../model/entity'
import type { EntityDetector } from './entityDetector'
import type { EntitySelector } from './entitySelector'

type ScreenPoint = { x: number; y: number }
type ScreenRect = { left: number; right: number; top: number; bottom: number }
type WorldToScreen = (point: THREE.Vector3) => ScreenPoint

/** CAD 实体框选实现 */
export class EntityFrameSelect extends FrameSelect<IEntity> {
  private camera: THREE.Camera | null = null
  private domElement: HTMLElement | null = null
  private worldToScreen: WorldToScreen | null = null
  private readonly boxScratch = new THREE.Box3()
  private readonly boxPoints = Array.from({ length: 8 }, () => new THREE.Vector3())
  private readonly screenPointScratch: ScreenPoint = { x: 0, y: 0 }
  private readonly pointScratch = new THREE.Vector3()
  private readonly segmentStartScratch: ScreenPoint = { x: 0, y: 0 }
  private readonly segmentEndScratch: ScreenPoint = { x: 0, y: 0 }

  constructor(
    private readonly viewManager: CADViewManager,
    private readonly detector: EntityDetector,
    private readonly selector: EntitySelector
  ) {
    super({
      onSelect: (items: IEntity[]) => {
        this.onSelect(items)
      },
      onHoverAdded: (items: IEntity[]) => {
        this.onHoverAdded(items)
      },
      onHoverRemoved: (items: IEntity[]) => {
        this.onHoverRemoved(items)
      },
    })
  }

  /**
   * 设置相机和 DOM 元素，由 CADController 在初始化时传入。
   *
   * @param camera - 用于计算屏幕投影的相机
   * @param domElement - 渲染视口的 DOM 元素，提供尺寸
   * @param worldToScreen - 世界坐标到画布屏幕坐标的转换函数，用于处理活动视口偏移
   */
  setContext(camera: THREE.Camera, domElement: HTMLElement, worldToScreen?: WorldToScreen) {
    this.camera = camera
    this.domElement = domElement
    this.worldToScreen = worldToScreen ?? null
  }

  private onSelect(items: IEntity[]) {
    this.selector.select(items)
  }

  private onHoverAdded(items: IEntity[]) {
    if (this.detector.getEnableHighlight()) {
      this.viewManager.hoverEntity(items)
    }
  }

  private onHoverRemoved(items: IEntity[]) {
    if (this.detector.getEnableHighlight()) {
      this.viewManager.cancelHoverEntity(items)
    }
  }

  protected getItemsInRect(rect: ScreenRect, mode: FrameSelectMode): IEntity[] {
    if (!this.camera || !this.domElement) {
      return []
    }

    const entities = this.viewManager.dataManager.entities
    const selected: IEntity[] = []

    for (const entity of entities) {
      if (this.viewManager.dataManager.getLayer(entity.layerId)?.visible === false) {
        continue
      }
      const object = this.viewManager.getEntityObject(entity)
      if (!object || !this.isObjectVisible(object, object)) {
        continue
      }

      object.updateWorldMatrix(true, false)
      const box = this.boxScratch.setFromObject(object)
      if (box.isEmpty()) {
        continue
      }

      const screenRect = this.getScreenRectFromBox(box)
      if (!screenRect) {
        continue
      }

      const hit = this.isHit(rect, screenRect, mode, object)
      if (hit) {
        selected.push(entity)
      }
    }

    return selected
  }

  private getScreenRectFromBox(box: THREE.Box3): ScreenRect | null {
    if (!this.camera || !this.domElement) {
      return null
    }

    const min = box.min
    const max = box.max
    const points = this.boxPoints
    points[0].set(min.x, min.y, min.z)
    points[1].set(min.x, min.y, max.z)
    points[2].set(min.x, max.y, min.z)
    points[3].set(min.x, max.y, max.z)
    points[4].set(max.x, min.y, min.z)
    points[5].set(max.x, min.y, max.z)
    points[6].set(max.x, max.y, min.z)
    points[7].set(max.x, max.y, max.z)

    let left = Infinity
    let right = -Infinity
    let top = Infinity
    let bottom = -Infinity

    for (const point of points) {
      if (!this.projectWorldPointToScreen(point, this.screenPointScratch)) {
        continue
      }
      const { x: sx, y: sy } = this.screenPointScratch
      left = Math.min(left, sx)
      right = Math.max(right, sx)
      top = Math.min(top, sy)
      bottom = Math.max(bottom, sy)
    }

    if (
      !Number.isFinite(left) ||
      !Number.isFinite(right) ||
      !Number.isFinite(top) ||
      !Number.isFinite(bottom)
    ) {
      return null
    }

    return { left, right, top, bottom }
  }

  private isHit(
    a: ScreenRect,
    b: ScreenRect,
    mode: FrameSelectMode,
    object: THREE.Object3D
  ): boolean {
    if (mode === FrameSelectMode.L2R) {
      return a.left <= b.left && a.right >= b.right && a.top <= b.top && a.bottom >= b.bottom
    }

    if (a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom) {
      return false
    }

    return this.isObjectIntersectRect(object, a)
  }

  private isObjectIntersectRect(object: THREE.Object3D, rect: ScreenRect): boolean {
    if (!this.camera || !this.domElement) {
      return false
    }

    let hasScreenSegment = false
    let hit = false
    object.traverse((child) => {
      if (hit || !this.isObjectVisible(child, object)) {
        return
      }

      const geometry = (child as { geometry?: THREE.BufferGeometry }).geometry
      if (!geometry) {
        return
      }

      const instanceStart = geometry.getAttribute('instanceStart')
      const instanceEnd = geometry.getAttribute('instanceEnd')
      if (instanceStart && instanceEnd) {
        for (let i = 0; i < Math.min(instanceStart.count, instanceEnd.count); i++) {
          const result = this.isScreenSegmentIntersectRect(
            child,
            instanceStart,
            instanceEnd,
            i,
            i,
            rect
          )
          hasScreenSegment ||= result.hasScreenSegment
          if (result.hit) {
            hit = true
            return
          }
        }
        return
      }

      const position = geometry.getAttribute('position')
      if (!position || position.count < 2) {
        return
      }

      const step = child instanceof THREE.LineSegments ? 2 : 1
      for (let i = 0; i + 1 < position.count; i += step) {
        const result = this.isScreenSegmentIntersectRect(child, position, position, i, i + 1, rect)
        hasScreenSegment ||= result.hasScreenSegment
        if (result.hit) {
          hit = true
          return
        }
      }
    })

    return !hasScreenSegment || hit
  }

  private isScreenSegmentIntersectRect(
    object: THREE.Object3D,
    startAttr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
    endAttr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
    startIndex: number,
    endIndex: number,
    rect: ScreenRect
  ): { hasScreenSegment: boolean; hit: boolean } {
    const hasStart = this.projectLocalPointToScreen(
      object,
      startAttr,
      startIndex,
      this.segmentStartScratch
    )
    const hasEnd = this.projectLocalPointToScreen(object, endAttr, endIndex, this.segmentEndScratch)

    if (!hasStart || !hasEnd) {
      return { hasScreenSegment: false, hit: false }
    }

    return {
      hasScreenSegment: true,
      hit: this.isSegmentIntersectRect(this.segmentStartScratch, this.segmentEndScratch, rect),
    }
  }

  private projectLocalPointToScreen(
    object: THREE.Object3D,
    attr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
    index: number,
    target: ScreenPoint
  ): boolean {
    if (!this.camera || !this.domElement) {
      return false
    }

    const point = this.pointScratch.set(attr.getX(index), attr.getY(index), attr.getZ(index))
    point.applyMatrix4(object.matrixWorld)
    return this.projectWorldPointToScreen(point, target)
  }

  private projectWorldPointToScreen(point: THREE.Vector3, target: ScreenPoint): boolean {
    if (!this.camera || !this.domElement) {
      return false
    }

    const screenPoint = this.worldToScreen
      ? this.worldToScreen(point)
      : {
          x: (point.project(this.camera).x * 0.5 + 0.5) * this.domElement.clientWidth,
          y: (-point.y * 0.5 + 0.5) * this.domElement.clientHeight,
        }
    if (!Number.isFinite(screenPoint.x) || !Number.isFinite(screenPoint.y)) {
      return false
    }

    target.x = screenPoint.x
    target.y = screenPoint.y
    return true
  }

  private isSegmentIntersectRect(start: ScreenPoint, end: ScreenPoint, rect: ScreenRect): boolean {
    if (this.isPointInRect(start, rect) || this.isPointInRect(end, rect)) {
      return true
    }

    const topLeft = { x: rect.left, y: rect.top }
    const topRight = { x: rect.right, y: rect.top }
    const bottomRight = { x: rect.right, y: rect.bottom }
    const bottomLeft = { x: rect.left, y: rect.bottom }

    return (
      this.isSegmentIntersectSegment(start, end, topLeft, topRight) ||
      this.isSegmentIntersectSegment(start, end, topRight, bottomRight) ||
      this.isSegmentIntersectSegment(start, end, bottomRight, bottomLeft) ||
      this.isSegmentIntersectSegment(start, end, bottomLeft, topLeft)
    )
  }

  private isPointInRect(point: ScreenPoint, rect: ScreenRect): boolean {
    return (
      point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom
    )
  }

  private isSegmentIntersectSegment(
    a: ScreenPoint,
    b: ScreenPoint,
    c: ScreenPoint,
    d: ScreenPoint
  ): boolean {
    const ab = this.getOrientation(a, b, c)
    const cd = this.getOrientation(a, b, d)
    const ca = this.getOrientation(c, d, a)
    const cb = this.getOrientation(c, d, b)

    if (ab === 0 && this.isPointOnSegment(c, a, b)) return true
    if (cd === 0 && this.isPointOnSegment(d, a, b)) return true
    if (ca === 0 && this.isPointOnSegment(a, c, d)) return true
    if (cb === 0 && this.isPointOnSegment(b, c, d)) return true

    return ab !== cd && ca !== cb
  }

  private getOrientation(a: ScreenPoint, b: ScreenPoint, c: ScreenPoint): -1 | 0 | 1 {
    const value = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y)
    if (Math.abs(value) < 1e-9) {
      return 0
    }
    return value > 0 ? 1 : -1
  }

  private isPointOnSegment(point: ScreenPoint, start: ScreenPoint, end: ScreenPoint): boolean {
    return (
      point.x <= Math.max(start.x, end.x) &&
      point.x >= Math.min(start.x, end.x) &&
      point.y <= Math.max(start.y, end.y) &&
      point.y >= Math.min(start.y, end.y)
    )
  }

  private isObjectVisible(object: THREE.Object3D, entityObject: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      // 仅忽略当前实体因合批隐藏的源对象；父级及子对象的真实显隐仍必须生效。
      const hiddenByBatch =
        current === entityObject && current.userData.cadStaticBatchHidden === true
      if (!current.visible && !hiddenByBatch) {
        return false
      }
      current = current.parent
    }
    return true
  }
}
